<script setup lang="ts">
import { isRunningInIosNativeApp } from '~/utils/browserInfo';

const { t, texts } = useTexts();
const { menuOpen } = storeToRefs(useLayoutStore());
const { bottomMenu } = storeToRefs(useLayoutStore());
const { isCookieConsentPopUpEnabled } = useFeatureFlagsNew();
const { isLayoutMobileOrTablet } = useLayout();

// Detecção de bots para bypass automático
const { isBot } = usePerformanceBotBypass();

const isDetailsOpen = ref<boolean>(false);
const cookiesOptIn = ref(['necessary']);

const details = ref({
  settings: {
    id: 1,
    title: t('cookieConsent.details.settings.title'),
  },
  about: {
    id: 2,
    title: t('cookieConsent.details.about.title'),
  },
});

const bottomMenuHeight = computed(() => {
  const bottomMenuHeight = bottomMenu.value.height;

  return bottomMenuHeight ? `${bottomMenuHeight}px` : '0px';
});

const cactusCookiesConsent = useCookie<string | string[]>(
  'cactusCookiesConsent',
  {
    default: () => '',
    path: '/',
    expires: new Date(new Date().setDate(new Date().getDate() + 365)),
  },
);

const isCookieConsentOpened = computed<boolean>(() => {
  return cactusCookiesConsent.value === 'opened';
});

const isCookieConsentChosen = computed<boolean>(() => {
  return cactusCookiesConsent.value !== '' && !isCookieConsentOpened.value;
});

const isConsentCookiePopupVisible = computed<boolean>(() => {
  // Nunca mostrar modal para bots (Googlebot, Lighthouse, GTmetrix)
  if (isBot.value) {
    return false;
  }

  // No app iOS o consentimento de rastreamento é feito via ATT (nativo), não
  // pelo banner de cookies. A Apple (5.1.2) sinaliza o prompt de cookies como
  // indício de tracking — então ele é suprimido dentro do app iOS.
  if (isRunningInIosNativeApp()) {
    return false;
  }

  if (!isCookieConsentPopUpEnabled.value) {
    return false;
  }

  if (isCookieConsentChosen.value) {
    return false;
  }

  if (isLayoutMobileOrTablet.value && menuOpen.value) {
    return false;
  }

  return isCookieConsentOpened.value;
});

function saveButton() {
  if (cookiesOptIn.value.length === 1) {
    cactusCookiesConsent.value = [
      'necessary',
      'performance',
      'functionality',
      'others',
    ];
  } else {
    cactusCookiesConsent.value = cookiesOptIn.value;
  }
}

function getSubcategories(): Array<{
  title: string;
  text: string;
  items: Array<{ name: string; expiration: string; description: string }>;
}> {
  return (texts as any).cookieConsent?.details?.settings?.subcategories || [];
}

onMounted(() => {
  // No app iOS o banner não aparece (consentimento via ATT), então não
  // inicializa o estado do cookie.
  if (isRunningInIosNativeApp()) {
    return;
  }

  if (!isCookieConsentChosen.value && !isCookieConsentOpened.value) {
    // Removido setTimeout para melhorar FCP - banner aparece imediatamente após hydration
    cactusCookiesConsent.value = 'opened';
  }
});
</script>

<template>
  <section
    v-if="isConsentCookiePopupVisible"
    :class="$style.cookieConsentWrapper"
  >
    <section
      id="cookieConsentContainer"
      :class="$style.cookieConsentContainer"
    >
      <p :class="$style.cookieConsentTitle">
        {{ $t('cookieConsent.title') }}
      </p>

      <p :class="$style.cookieConsentDesc">
        {{ $t('cookieConsent.desc') }}
      </p>

      <section
        v-if="isDetailsOpen"
        :class="$style.checkboxSection"
      >
        <BaseFormCheckbox
          v-model="cookiesOptIn"
          disabled
          :label="$rt(getSubcategories()[0]!.title)"
          value="necessary"
          @click.prevent.stop="() => {}"
        />
        <BaseFormCheckbox
          v-model="cookiesOptIn"
          :label="$rt(getSubcategories()[1]!.title)"
          value="performance"
        />
        <BaseFormCheckbox
          v-model="cookiesOptIn"
          :label="$rt(getSubcategories()[2]!.title)"
          value="functionality"
        />
        <BaseFormCheckbox
          v-model="cookiesOptIn"
          :label="$rt(getSubcategories()[3]!.title)"
          value="others"
        />
      </section>

      <section
        v-if="isDetailsOpen"
        :class="$style.detailsSection"
      >
        <HeadlessTabGroup>
          <HeadlessTabList :class="$style.detailsTabList">
            <HeadlessTab
              v-for="{ title } in Object.values(details)"
              :key="title"
              v-slot="{ selected }"
              as="template"
            >
              <button
                :class="[
                  $style.detailsBtn,
                  selected ? $style.btnSelected : $style.btnUnselected,
                ]"
              >
                {{ title }}
              </button>
            </HeadlessTab>
          </HeadlessTabList>
          <HeadlessTabPanels>
            <HeadlessTabPanel
              v-for="{ id } in Object.values(details)"
              :key="id"
            >
              <section v-if="id === 2">
                <p
                  :class="$style.categoryText"
                  v-html="$t('cookieConsent.details.about.text')"
                />
              </section>

              <!-- Subcategories -->
              <HeadlessTabGroup v-if="id === 1">
                <section :class="$style.categoryWrapper">
                  <div :class="$style.absoluteWrapper">
                    <HeadlessTabList :class="$style.categoryTabList">
                      <HeadlessTab
                        v-for="(item, idSub) in getSubcategories()"
                        :key="idSub"
                        v-slot="{ selected }"
                        as="template"
                      >
                        <button
                          :class="[
                            $style.categoryBtn,
                            selected
                              ? $style.catBtnSelected
                              : $style.catBtnUnselected,
                          ]"
                        >
                          {{ $rt(item.title) }}
                        </button>
                      </HeadlessTab>
                    </HeadlessTabList>
                  </div>
                </section>

                <HeadlessTabPanels>
                  <section :class="$style.detailsWrapper">
                    <HeadlessTabPanel
                      v-for="(subcat, idPanel) in getSubcategories()"
                      :key="idPanel"
                      :class="$style.detailsPanel"
                    >
                      <p :class="$style.detailsText">
                        {{ $rt(subcat.text) }}
                      </p>
                      <table :class="$style.categoryTable">
                        <thead :class="$style.categoryTHead">
                          <tr>
                            <th> {{ $t('cookieConsent.table.name') }} </th>
                            <th>
                              {{ $t('cookieConsent.table.expiration') }}
                            </th>
                            <th>
                              {{ $t('cookieConsent.table.description') }}
                            </th>
                          </tr>
                        </thead>
                        <tbody :class="$style.categoryTBody">
                          <tr
                            v-for="item in subcat.items"
                            :key="$rt(item.name)"
                          >
                            <td> {{ $rt(item.name) }} </td>
                            <td> {{ $rt(item.expiration) }} </td>
                            <td>
                              {{ $rt(item.description) }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </HeadlessTabPanel>
                  </section>
                </HeadlessTabPanels>
              </HeadlessTabGroup>
            </HeadlessTabPanel>
          </HeadlessTabPanels>
        </HeadlessTabGroup>
      </section>

      <div :class="$style.cookieButtons">
        <button
          :class="$style.detailsButton"
          @click="isDetailsOpen = !isDetailsOpen"
        >
          <NuxtIcon name="gear" />
          <span v-if="!isDetailsOpen">
            {{ $t('cookieConsent.show_details') }}
          </span>
          <span v-else> {{ $t('cookieConsent.hide_details') }} </span>
        </button>
        <button
          :class="$style.closeButton"
          @click="cactusCookiesConsent = ['necessary']"
        >
          {{ $t('cookieConsent.refuse_all') }}
        </button>
        <button
          :class="$style.okButton"
          @click="saveButton"
        >
          <span v-if="cookiesOptIn.length > 1">
            {{ $t('cookieConsent.save_close') }}
          </span>
          <span v-else>
            {{ $t('cookieConsent.accept_all') }}
          </span>
        </button>
      </div>
    </section>
  </section>
</template>

<style module lang="scss">
.cookieConsentWrapper {
  @apply fixed top-[initial] left-0 bottom-0 z-[700] w-full max-w-lg mx-auto p-2 pt-0 text-2xs lg:text-xs text-texts;

  margin-bottom: v-bind(bottomMenuHeight);

  @media print {
    & {
      display: none;
    }
  }

  .cookieConsentContainer {
    @apply relative w-full mx-auto py-3 px-4 rounded-lg bg-bg-secondary lg:py-4 lg:px-6 border border-solid border-black/10;

    /* Otimizado: box-shadow mais leve para melhor performance */
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    &:after {
      @apply content-[''] absolute top-0 left-0 w-full h-full bg-black/20 z-[1];
    }

    .cookieConsentTitle {
      @apply w-full pb-1.5 text-base lg:text-xl font-bold relative z-[2];
    }

    .cookieConsentDesc {
      @apply font-medium leading-4 mb-3 relative z-[2];
    }

    .checkboxSection {
      @apply mt-2 mb-4 flex flex-col gap-1 relative z-[2];
    }

    .detailsSection {
      @apply relative mt-2 pt-2 bg-bg-secondary/10 rounded-lg mb-4 z-[2];

      .detailsTabList {
        @apply flex space-x-1 rounded-t-lg bg-texts/10 p-1 -mt-2;

        .detailsBtn {
          @apply w-full rounded-lg py-2 font-semibold leading-5 focus:outline-none;
        }

        .btnSelected {
          @apply bg-button-bg text-button-text shadow;
        }

        .btnUnselected {
          @apply hover:bg-button-bg/10;
        }
      }

      .categoryWrapper {
        @apply relative w-full max-w-lg;

        .absoluteWrapper {
          @apply absolute w-full;

          .categoryTabList {
            @apply h-10 flex space-x-1 p-1 pb-1.5 border-b border-solid border-texts/20;

            .categoryBtn {
              @apply w-full rounded-full py-0 px-0.5 text-[.55rem] font-medium leading-none bg-texts/10 focus:outline-none;
            }

            .catBtnSelected {
              @apply bg-bg-primary text-texts shadow;
            }

            .catBtnUnselected {
              @apply hover:bg-texts/15;
            }
          }
        }
      }

      .categoryText {
        @apply pt-2 pb-4 px-4 max-h-60 overflow-auto font-medium bg-texts/10 rounded-b-lg;

        a {
          @apply text-primary underline;
        }
      }

      .detailsWrapper {
        @apply pt-10 h-56 md:h-60 bg-texts/10 rounded-b-lg;

        .detailsPanel {
          @apply h-full overflow-auto px-3;

          .detailsText {
            @apply mt-2 font-medium;
          }

          .categoryTable {
            @apply my-3 table-fixed border-collapse border border-solid border-bg-primary text-texts/80;

            .categoryTHead {
              th {
                @apply bg-bg-primary/20 p-1 border border-solid border-bg-primary text-2xs font-semibold;
              }
            }

            .categoryTBody {
              td {
                @apply p-1 border border-solid border-bg-primary text-xs;
              }
            }
          }
        }
      }
    }

    .cookieButtons {
      @apply w-full flex justify-end lg:flex-row gap-1 lg:gap-2 mt-2 relative z-[2];

      button {
        @apply font-bold text-2xs lg:text-xs py-1 px-3 rounded-full leading-3 border border-solid border-texts;
      }

      .detailsButton {
        @apply flex items-center gap-1 float-left mr-auto px-0 border-none;

        span:last-child {
          @apply hidden xs:block;
        }

        svg {
          @apply text-lg mt-px;
        }
      }

      .closeButton {
        @apply bg-transparent;
      }

      .okButton {
        @apply bg-button-bg text-button-text border-button-bg;
      }
    }
  }

  ::-webkit-scrollbar {
    width: 5px;
    height: 5px;
    @media (max-width: 768px) {
      width: 0;
    }
  }

  ::-webkit-scrollbar-track {
    @apply bg-transparent;
  }

  ::-webkit-scrollbar-thumb {
    @apply border-transparent;
  }
}
</style>
